import {
    Dispatch,
    ReactNode,
    SetStateAction,
    createContext,
    useState,
} from 'react';
import { UseFormMethods, useForm } from 'react-hook-form';
import { SelectOption } from '../components/Select';
import Seller from '../shared/interfaces/seller.interface';
import { EditManySellersActions } from '../constants/editManySeller';
import sellerStatus from '../constants/sellerStatus';

export interface IEditManyHandler {
    execute(): Promise<void>;
}

interface SellerFilterFields {
    filterStatus: Array<SelectOption<string>>;
    filterContract: Array<SelectOption<string | boolean>>;
    filterAttachment: SelectOption<boolean> | null;
    filterComercialConsultants: Array<SelectOption<number>>;
    filterStates: Array<SelectOption<string>>;
    filterCities: Array<SelectOption<string>>;
    filterStartDate: string;
    filterEndDate: string;
    filterTerm: string;
}

interface SellerEditManyFields {
    action?: SelectOption<EditManySellersActions>;
    newStatus?: SelectOption<sellerStatus>;
    useFilters: boolean;
}

interface SellerContextValue {
    showBlockIntegratorModal: boolean;
    setShowBlockIntegratorModal: Dispatch<SetStateAction<boolean>>;
    showUnblockIntegratorModal: boolean;
    setShowUnblockIntegratorModal: Dispatch<SetStateAction<boolean>>;
    showDigitalContractModal: boolean;
    setShowDigitalContractModal: Dispatch<SetStateAction<boolean>>;
    showFilterSection: boolean;
    setShowFilterSection: Dispatch<SetStateAction<boolean>>;
    showEditManyModal: boolean;
    setShowEditManyModal: Dispatch<SetStateAction<boolean>>;
    showEditManyModalInsertValue: boolean;
    setShowEditManyModalInsertValue: Dispatch<SetStateAction<boolean>>;
    sellerOnDigitalContractUpdate: number | null;
    setSellerOnDigitalContractUpdate: Dispatch<SetStateAction<number | null>>;
    selectedSeller: Seller | null;
    setSelectedSeller: Dispatch<SetStateAction<Seller | null>>;
    filterForm: UseFormMethods<SellerFilterFields>;
    actionToEditForm: UseFormMethods<SellerEditManyFields>;
    showInternalNotesModal: boolean;
    setShowInternalNotesModal: Dispatch<SetStateAction<boolean>>;
    selectedEditManyIds: number[];
    setSelectedEditManyIds: Dispatch<SetStateAction<number[]>>;
}

interface SellersProviderProps {
    children: ReactNode;
}

export const SellersContext = createContext<SellerContextValue>(
    {} as SellerContextValue,
);

export function SellersProvider({ children }: SellersProviderProps) {
    const [showBlockIntegratorModal, setShowBlockIntegratorModal] =
        useState<boolean>(false);

    const [showUnblockIntegratorModal, setShowUnblockIntegratorModal] =
        useState<boolean>(false);

    const [showDigitalContractModal, setShowDigitalContractModal] =
        useState<boolean>(false);

    const [sellerOnDigitalContractUpdate, setSellerOnDigitalContractUpdate] =
        useState<number | null>(null);

    const [selectedSeller, setSelectedSeller] = useState<Seller | null>(null);

    const [showFilterSection, setShowFilterSection] = useState<boolean>(false);

    const [showInternalNotesModal, setShowInternalNotesModal] =
        useState<boolean>(false);

    const [showEditManyModal, setShowEditManyModal] = useState<boolean>(false);

    const [showEditManyModalInsertValue, setShowEditManyModalInsertValue] = useState<boolean>(false);

    const [selectedEditManyIds, setSelectedEditManyIds] = useState<number[]>([]);
    const actionToEditForm = useForm<SellerEditManyFields>({
        shouldUnregister: false,
        defaultValues: {
            action: undefined,
            useFilters: false,
        },
    })
    
    const filterForm = useForm<SellerFilterFields>({
        shouldUnregister: false,
        defaultValues: {
            filterTerm: '',
            filterStatus: [],
            filterContract: [],
            filterComercialConsultants: [],
            filterStates: [],
            filterCities: [],
            filterAttachment: null,
            filterStartDate: '',
            filterEndDate: '',
        },
    });

    const value: SellerContextValue = {
        showBlockIntegratorModal,
        setShowBlockIntegratorModal,
        showUnblockIntegratorModal,
        setShowUnblockIntegratorModal,
        showDigitalContractModal,
        setShowDigitalContractModal,
        showFilterSection,
        setShowFilterSection,
        showEditManyModal,
        setShowEditManyModal,
        showEditManyModalInsertValue,
        setShowEditManyModalInsertValue,
        sellerOnDigitalContractUpdate,
        setSellerOnDigitalContractUpdate,
        selectedSeller,
        setSelectedSeller,
        filterForm,
        actionToEditForm,
        showInternalNotesModal,
        setShowInternalNotesModal,
        selectedEditManyIds,
        setSelectedEditManyIds,
    };

    return (
        <SellersContext.Provider value={value}>
            {children}
        </SellersContext.Provider>
    );
}
